import { useEffect, useRef, useMemo } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import avo from 'analytics/avo';
import useDebouncedValue from 'hooks/useDebouncedValue';
import { ALGOLIA_INDEX_TO_SORT_ORDER } from 'commons/constants';
import { getSortInAvoFormat } from 'components/consumer/ProductListingPage/utils';
import useAdvancedSearchContext from 'components/consumer/consumer-search/advanced-search-next/hooks/useAdvancedSearchContext';
import { PAGE_TYPE, URL_PARAM, getCategoryPageTypeConfig } from '../categoryPageTypes';
const getAlgoliaFiltersInAvoFormat = ({
  refinementList = {},
  selectedFacets = {},
  isReadyToShipActive,
  minWidth,
  maxWidth,
  searchQuery,
  urlParams
}) => {
  const facetFilters = Object.entries(selectedFacets).flatMap(([type, values]) => [...values].map(value => ({
    type,
    value
  })));
  const ownsParam = param => urlParams.includes(param);
  return [...Object.entries(refinementList).flatMap(([type, values]) => Array.isArray(values) ? values.map(value => ({
    type,
    value
  })) : []), ...facetFilters, ...(searchQuery && ownsParam(URL_PARAM.SEARCH) ? [{
    type: 'search',
    value: searchQuery
  }] : []), ...(isReadyToShipActive && ownsParam(URL_PARAM.QUICKSHIP) ? [{
    type: 'quickship',
    value: 'true'
  }] : []), ...(minWidth && ownsParam(URL_PARAM.WIDTH_RANGE) ? [{
    type: 'width_min',
    value: String(minWidth)
  }] : []), ...(maxWidth && ownsParam(URL_PARAM.WIDTH_RANGE) ? [{
    type: 'width_max',
    value: String(maxWidth)
  }] : [])];
};
const mapProductsToAvo = (products, pageName) => products.map(product => {
  const firstVariant = product.variantsToShow?.[0];
  return {
    category: pageName,
    name: product.name,
    shippingCarrier: 'Custom',
    shippingMethod: 'Curbside',
    currency: 'USD',
    price: parseFloat(firstVariant?.price) || 0,
    fullPrice: parseFloat(firstVariant?.originalPrice) || 0,
    productId: product.id,
    quantity: 1,
    sku: firstVariant?.sku || '',
    isFlashSale: !!firstVariant?.isFlashSale,
    googleProductCategory: product?.googleProductCategory || ''
  };
});
const useCategoryAlgoliaAnalytics = ({
  pageId,
  pageName: pageNameProp,
  products,
  pageType = PAGE_TYPE.CATEGORY
}) => {
  const {
    urlParams,
    defaultSortOrder,
    analyticsListId,
    analyticsCategoryId,
    analyticsAllowNullCategoryId
  } = getCategoryPageTypeConfig(pageType);
  const {
    indexUiState
  } = useInstantSearch();
  const {
    currentProductIndex,
    isReadyToShipActive,
    minWidth,
    maxWidth,
    searchQuery,
    selectedFacets
  } = useAdvancedSearchContext();
  const listViewedRef = useRef(false);
  const lastTrackedFilterStateRef = useRef(null);
  const pageName = pageNameProp || analyticsListId;
  // Page types with no Alexandria page fall back to the descriptor's id;
  // without one, `NaN` suppresses every event below — unless the descriptor
  // explicitly opts in to a null id, which passes both NaN gates the same way
  // a legacy `pageId: null` did (`Number.isNaN(null) === false`).
  const parsedCategoryId = parseInt(pageId ?? analyticsCategoryId, 10);
  const categoryId = Number.isNaN(parsedCategoryId) && analyticsAllowNullCategoryId ? null : parsedCategoryId;
  const filterStateKey = useMemo(() => JSON.stringify({
    refinementList: indexUiState?.refinementList || {},
    selectedFacets: Object.fromEntries(Object.entries(selectedFacets || {}).map(([key, values]) => [key, [...values].sort()])),
    searchQuery: searchQuery || '',
    isReadyToShipActive,
    minWidth,
    maxWidth,
    sortIndex: currentProductIndex
  }), [indexUiState?.refinementList, selectedFacets, searchQuery, isReadyToShipActive, minWidth, maxWidth, currentProductIndex]);
  const debouncedFilterStateKey = useDebouncedValue(filterStateKey, 500);
  const sortOrder = ALGOLIA_INDEX_TO_SORT_ORDER[currentProductIndex] || defaultSortOrder;
  const filters = useMemo(() => getAlgoliaFiltersInAvoFormat({
    refinementList: indexUiState?.refinementList,
    selectedFacets,
    isReadyToShipActive,
    minWidth,
    maxWidth,
    searchQuery,
    urlParams
  }), [indexUiState?.refinementList, selectedFacets, isReadyToShipActive, minWidth, maxWidth, searchQuery, urlParams]);
  useEffect(() => {
    if (products?.length > 0 && !listViewedRef.current) {
      listViewedRef.current = true;
      try {
        if (!Number.isNaN(categoryId)) {
          avo.productListViewed({
            categoryId,
            listId: pageName,
            category: pageName,
            filters,
            products: mapProductsToAvo(products, pageName)
          });
        }
      } catch {
        // Ignore if avo not initialized
      }
    }
  }, [products, categoryId, pageName, filters]);
  useEffect(() => {
    if (!pageName || debouncedFilterStateKey === null || !products?.length) {
      return;
    }
    if (debouncedFilterStateKey === lastTrackedFilterStateRef.current) {
      return;
    }
    lastTrackedFilterStateRef.current = debouncedFilterStateKey;
    if (Number.isNaN(categoryId)) {
      return;
    }
    try {
      avo.productListFiltered({
        categoryId,
        listId: pageName,
        category: pageName,
        filters,
        sorts: getSortInAvoFormat(sortOrder)?.filter(Boolean) ?? [],
        products: mapProductsToAvo(products, pageName)
      });
    } catch {
      // Ignore if avo not initialized
    }
  }, [pageName, filters, products, categoryId, sortOrder, debouncedFilterStateKey]);
};
export default useCategoryAlgoliaAnalytics;